import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useTheme, useFlowPortalContent, defaultTheme } from '../../context/PortalThemeContext';
import { ClaimStates } from './ClaimStates';
import { ItemCard } from '../shared/ProductVariantCards';
import NavButtons from '../returns/NavButtons';
import { fetchWithSessionToken } from '../shared/fetchWithSessionToken';
import { fallbackString } from '../shared/FallbackUtils';
import { CHAT_API_BASE } from '../../config/api';
import './ClaimResolutionSelection.css';

const ClaimResolutionSelection = ({
    selectedItems,
    claimReason,
    onResolutionSelected,
    onStepChange,
    shopifyProducts,
    orderDetails,
    flowType = 'CLAIM'
}) => {
    const theme = useTheme();
    const defaultSettings = defaultTheme.flowPortalContentSettings.CLAIM.workflowSelection;

    // Use the new flow-specific hook
    const flowPortalContent = useFlowPortalContent(flowType);
    const workflowSelectionSettings = flowPortalContent?.workflowSelection || defaultSettings;

    const [claimFlow, setClaimFlow] = useState(null);
    const [selectedResolution, setSelectedResolution] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingError, setLoadingError] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        const getClaimFlow = async () => {
            setIsLoading(true);
            setLoadingError(null);

            const shopId = theme.store;

            try {
                const url = new URL(`${CHAT_API_BASE}/workflow/${shopId}/claim_flow`);

                const response = await fetchWithSessionToken(url.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    setLoadingError('No options available. Please try again later.');
                    setIsLoading(false);
                    return;
                }

                const claimFlowJson = await response.json();
                const claimFlow = {
                    shopId: claimFlowJson.shopId,
                    workflowId: claimFlowJson.workflowId,
                    name: claimFlowJson.name,
                    startState: claimFlowJson.startState,
                    states: (claimFlowJson.states || []).map((state) => ({
                        stateId: state.stateId,
                        name: state.name,
                        title: state.title,
                        type: state.type,
                        actionCode: state.actionCode,
                        transitions: (state.transitions || []).map((transition) => ({
                            name: transition.name,
                            toStateId: transition.toStateId,
                            showOptionalText: transition.showOptionalText,
                            allowImageUpload: transition.allowImageUpload,
                        })),
                        isFinal: state.isFinal
                    }))
                };

                setClaimFlow(claimFlow);
                setIsLoading(false);

            } catch (err) {
                console.error('claimFlow error:', err);
                setLoadingError('Something went wrong. Please try again.');
            }

            setIsLoading(false);
        };
        getClaimFlow();
    }, [theme.store]);

    const handleNext = () => {
        // Check if all items have resolutions selected

        if (!selectedResolution) {
            setError(true);
            return;
        }

        // Create a complete claim reason object with optional text and images
        const completeClaimReason = {
            ...claimReason,
            resolution: selectedResolution,
        };

        onResolutionSelected(completeClaimReason);
    };

    const handleBack = () => {
        onStepChange(ClaimStates.SELECT_CLAIM_ITEMS);
    };

    if (!selectedItems || selectedItems.length === 0) {
        console.warn('ClaimResolutionSelection rendered without selected items');
        return null;
    }

    // Get the resolution options from the step-what-do state
    const resolutionState = claimFlow?.states?.find(state => state.stateId === 'step-what-do');
    const resolutionOptions = resolutionState?.transitions || [];

    return (
        <>
            <div className="d-flex-column flex-md-row align-items-top gap-5 gap-md-5"
                style={{ padding: '1rem' }}>

                {/* Column 1 - Summary */}
                <div style={{ flex: '1', minWidth: '16rem', width: '100%' }}>
                    <div className="summary-card">
                        <h2 className="card-header-text">
                            Claim Summary
                        </h2>

                        <div >
                            {selectedItems?.map((item, index) => {
                                const matchedProduct = shopifyProducts?.find((p) => p.id === item.product_id);

                                return (
                                    <ItemCard
                                        key={item.id}
                                        item={item}
                                        shopifyProduct={matchedProduct}
                                        horizontal={true}
                                        cardStyle={false}
                                        flowType="CLAIM"
                                    />
                                );
                            })}
                        </div>

                        <div className="summary-content">
                            <div className="resolution-summary">
                                <p className='mb-2'><strong>Claim Reason:</strong> {claimReason?.name} {claimReason?.optionalText && <span>- {claimReason.optionalText}</span>}</p>
                                <p className='mb-2'><strong>Resolution:</strong> <span>{selectedResolution ? selectedResolution : 'Not selected'}</span></p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Column 2 - Resolution options */}
                <div className='w-100' style={{ flex: '1' }}>

                    {isLoading && (
                        <div className="spinner" />
                    )}

                    <h1 className="title mb-4">
                        {fallbackString(resolutionState?.title, 'What would you like to do?')}
                    </h1>

                    {claimFlow && !loadingError && (
                        <div >
                            <div className="resolution-options">
                                <Form className="my-radio-group">
                                    {resolutionOptions.map((option, optionIndex) => {
                                        const id = `option_${option.name}_${optionIndex}`;
                                        const isSelected = selectedResolution === option.name;

                                        return (
                                            <div key={id} style={{ width: '100%' }}>
                                                <label
                                                    htmlFor={id}
                                                    className={`form-check full-width-label ${isSelected ? 'selected' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <input
                                                        type="radio"
                                                        id={id}
                                                        name={`reason_${option.name}`}
                                                        value={option.name}
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            setError(false);
                                                            setSelectedResolution(option.name);
                                                        }}
                                                        className="form-check-input"
                                                    />

                                                    <span className="form-check-label">
                                                        {option.name}
                                                    </span>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </Form>
                            </div>
                        </div>
                    )}
                </div>

            </div>

            <div className="sticky-bottom" style={{ marginTop: '2rem', zIndex: '100' }}>
                {error && (
                    <p className="no-selection-error">Please select a resolution for all items to proceed.</p>
                )}
                <NavButtons
                    handleBack={handleBack}
                    handleNext={handleNext}
                    disabled={!selectedResolution}
                    onDisabledClick={() => setError(true)}
                />
            </div>
        </>
    );
};

export default ClaimResolutionSelection;
